import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ProductList } from '../../models/product-list.model';
import { ProductDetailViewModel } from '../../models/product-detail-view-model';
import { PlatformService, PlatformEnum, PanAppState } from '@panamax/app-state';
import { FEATURES } from '../../../shared/constants/splitio-features';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-product-detail-description',
  templateUrl: './product-detail-description.component.html',
  styleUrls: ['./product-detail-description.component.scss'],
})
export class ProductDetailDescriptionComponent
  implements OnChanges, AfterViewChecked, AfterViewInit, OnDestroy
{
  @ViewChild('moreBtn') moreBtn: ElementRef;
  @ViewChild('lessBtn') lessBtn: ElementRef;
  @ViewChild('content') overviewRef: ElementRef;
  @ViewChild('portionsBox') portionsBoxRef: ElementRef;

  @Input() product: ProductDetailViewModel;

  public platformType: PlatformEnum;
  public platformEnum = PlatformEnum;
  features: string[];
  benefits: string[];
  boxDimensions: ProductList[] = [];
  portions: ProductList[] = [];
  hasOverviewSection = false;
  firstLoad = false;

  pricePerPortionFeatureFlagSubscription: Subscription;

  constructor(
    readonly platformService: PlatformService,
    private renderer: Renderer2,
    readonly panAppState: PanAppState,
  ) {}

  ngAfterViewInit(): void {
    this.firstLoad = false;
  }

  ngAfterViewChecked(): void {
    const height = this.portionsBoxRef?.nativeElement.clientHeight;
    if (
      height > 0 &&
      this.overviewRef?.nativeElement.clientHeight > height &&
      !this.firstLoad &&
      this.platformService.platformType !== PlatformEnum.mobile
    ) {
      this.firstLoad = true;
      this.renderer?.setStyle(this.lessBtn?.nativeElement, 'display', 'block');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.product?.currentValue) {
      this.formatFeatures();
      this.formatBenefits();
      this.formatPortions();
      this.formatBoxDimensions();
      this.hasOverviewSection = this.hasOverview();
    }
  }

  formatFeatures(): void {
    this.features = this.splitContent(this.product?.detail?.productAttributes);
  }

  formatBenefits(): void {
    this.benefits = this.splitContent(this.product?.detail?.benefits);
  }

  formatBoxDimensions(): void {
    const {
      purchasePackDepth,
      purchasePackWidth,
      purchasePackHeight,
      volume,
      volumeUnits,
      storageCode,
      storageCodeDescription,
    } = this.product?.detail || {};

    this.boxDimensions = [
      {
        keyLabel: 'i18n.productDetail.length',
        value: `${purchasePackDepth} in.`,
        visible: !!purchasePackDepth,
      },
      {
        keyLabel: 'i18n.productDetail.width',
        value: `${purchasePackWidth} in.`,
        visible: !!purchasePackWidth,
      },
      {
        keyLabel: 'i18n.productDetail.height',
        value: `${purchasePackHeight} in.`,
        visible: !!purchasePackHeight,
      },
      {
        keyLabel: 'i18n.productDetail.volume',
        value: `${volume} ${volumeUnits}`,
        visible: !!volume,
      },
      {
        keyLabel: 'i18n.productDetail.storage',
        value: `${storageCodeDescription} ${storageCode}`,
        visible: !!storageCodeDescription,
      },
    ].filter(boxDimension => boxDimension.visible);
  }

  formatPortions(): void {
    const {
      servingsPerCase,
      servingSize,
      yield: productYield,
      yieldUOM,
    } = this.product?.detail || {};

    const portions = [
      {
        keyLabel: 'i18n.productDetail.servingCase',
        value: servingsPerCase,
        visible: false,
      },
      {
        keyLabel: 'i18n.productDetail.servingSize',
        value: servingSize,
        visible: false,
      },
      {
        keyLabel: 'i18n.productDetail.packSize',
        value: this.product?.summary?.salesPackSize,
      },
    ];
    this.portions = portions.filter(portion => !!portion.value);

    this.pricePerPortionFeatureFlagSubscription?.unsubscribe();
    this.pricePerPortionFeatureFlagSubscription = this.panAppState
      .feature$([FEATURES.split_global_price_per_portion.name])
      .pipe(take(1))
      .subscribe(pricePerPortionFeatureEnabled => {
        if (pricePerPortionFeatureEnabled) {
          portions.push({
            keyLabel: 'i18n.productDetail.portionPrice',
            value: this.product?.costBreakdown,
          });
          this.portions = portions
            .filter(portion => !!portion.value)
            .filter(portion => portion.visible !== false);
        }
      });
  }

  splitContent(s: string): string[] {
    return s ? s.split(/~/).filter(a => a.trim() !== '') : null;
  }

  hasOverview = (): boolean =>
    !!this.product?.detail.additionalDescription ||
    !!this.product?.detail.countryOfOriginGrownHarvested ||
    !!this.product?.detail.countryOfOriginProcessed;

  viewMore() {
    this.renderer?.setStyle(this.moreBtn?.nativeElement, 'display', 'none');
    this.renderer?.setStyle(this.lessBtn?.nativeElement, 'display', 'block');
    this.renderer?.removeClass(this.overviewRef?.nativeElement, 'hide-content');
    this.renderer?.setStyle(this.overviewRef?.nativeElement, 'height', 'auto');
  }

  viewLess() {
    this.renderer?.setStyle(this.lessBtn?.nativeElement, 'display', 'none');
    this.renderer?.setStyle(this.moreBtn?.nativeElement, 'display', 'block');
    this.renderer?.addClass(this.overviewRef?.nativeElement, 'hide-content');
    this.renderer?.setStyle(
      this.overviewRef?.nativeElement,
      'height',
      this.portionsBoxRef?.nativeElement?.clientHeight - 50 + 'px',
    );
  }

  ngOnDestroy(): void {
    this.pricePerPortionFeatureFlagSubscription?.unsubscribe();
  }
}
